import { useEffect, useRef, useState } from "react";
import Dialog from "./Dialog";
import Icon from "./Icon";
import { auth } from "../../firebase/config.js";
import { PRO_PRICE, PRO_TRIAL_DAYS } from "../../plans.js";
import { MIN_AUDIO_MS, MAX_AUDIO_SECONDS, blobToBase64, canRecord, clock, startRecording } from "../../voice.js";
import { CARD_ROWS, MAX_MESSAGE, askAssistant, buildContext, buildHistory, planAction, readReply, suggestions } from "../../assistant.js";
import { STATS_TTL_MS, loadStatsContext } from "../../assistantStats.js";
import "../../panel-import.css";
import "../../panel-assistant.css";

// Una propuesta del asistente: muestra cómo queda y se guarda solo si el dueño confirma.
const ActionCard = ({ action, data, onState }) => {
    const [all, setAll] = useState(false);
    const [busy, setBusy] = useState(false);
    const plan = planAction(action, { business: data.business, products: data.products, categories: data.categories });
    const rows = all ? plan.rows : plan.rows.slice(0, CARD_ROWS);
    const pending = action.estado === "pendiente";
    // Lo que se ve una vez resuelta es lo que se propuso, no cómo quedaría ahora.
    const title = pending ? plan.title : action.resumen;

    const confirm = async () => {
        setBusy(true);
        let ok = false;
        if (plan.tipo === "precios") ok = await data.changePrices(plan.update, plan.description);
        else if (plan.tipo === "agotado") ok = await data.setAgotadoMany(plan.update, plan.agotado);
        else if (plan.tipo === "datos") ok = await data.saveBusiness(plan.fields);
        else ok = await data.setOrders(plan.activar);
        setBusy(false);
        if (ok) onState("confirmada", plan.description);
    }

    const canUndo = action.estado === "confirmada" && action.tipo === "precios" && data.lastPriceChange && data.lastPriceChange.description === action.description;

  return (
    <div className={"lp-ai-card is-" + action.estado}>
        <div className="lp-ai-card-head">
            <span className="lp-ai-card-ic" aria-hidden="true"><Icon name={action.tipo === "precios" ? "money" : action.tipo === "agotado" ? "box" : action.tipo === "pedidos" ? "whatsapp" : "store"} size={16}></Icon></span>
            <strong>{title}</strong>
        </div>
        {pending && rows.length > 0 &&
            <ul className="lp-ai-rows">
                {rows.map((r) => (
                    <li key={r.key}>
                        <span className="lp-ai-row-name">{r.label}{r.sub && <span className="lp-panel-muted">{r.sub}</span>}</span>
                        <span className="lp-ai-row-vals">
                            <s className="lp-panel-muted">{r.before}</s>
                            <Icon name="arrow" size={13} strokeWidth={2.4}></Icon>
                            <strong className={r.up === true ? "is-up" : r.up === false ? "is-down" : ""}>{r.after}</strong>
                        </span>
                    </li>
                ))}
            </ul>
        }
        {pending && plan.rows.length > CARD_ROWS && <button type="button" className="lp-text-link lp-ai-more" onClick={() => setAll(!all)}>{all ? "Ver menos" : `Ver los ${plan.rows.length}`}</button>}
        {pending && plan.notes?.map((n) => <p key={n} className="lp-ai-note">{n}</p>)}
        {pending && plan.problem && <p className="lp-imp-note is-warn"><Icon name="alert" size={16} strokeWidth={2.4}></Icon><span>{plan.problem}</span></p>}

        {pending && !plan.problem &&
            <div className="lp-ai-card-actions">
                <button type="button" className="lp-btn lp-btn-primary lp-panel-btn-sm" disabled={busy} onClick={confirm}>{busy ? "Guardando..." : <><Icon name="check" size={16} strokeWidth={2.4}></Icon> {plan.confirmLabel}</>}</button>
                <button type="button" className="lp-btn lp-btn-secondary lp-panel-btn-sm" disabled={busy} onClick={() => onState("cancelada")}>Cancelar</button>
            </div>
        }
        {pending && plan.problem && <button type="button" className="lp-text-link lp-ai-more" onClick={() => onState("descartada")}>Descartar</button>}
        {action.estado === "confirmada" &&
            <p className="lp-ai-done"><Icon name="check" size={16} strokeWidth={2.6}></Icon> Hecho
                {canUndo && <button type="button" className="lp-text-link" onClick={data.undoPriceChange}>Deshacer</button>}
            </p>
        }
        {(action.estado === "cancelada" || action.estado === "descartada") && <p className="lp-ai-done is-off">No se cambió nada.</p>}
    </div>
  )
}

const Upsell = ({ open, onClose }) => (
    <Dialog open={open} title="Asistente con IA" onClose={onClose}>
        <div className="lp-panel-stack lp-imp-gap">
            <p className="lp-panel-muted">Con el plan Pro le pedís los cambios a tu menú como si le escribieras a alguien por WhatsApp.</p>
            <ul className="lp-imp-feat">
                <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="money" size={18}></Icon></span><span><strong>"Subí las pizzas un 10%"</strong><span className="lp-panel-muted">Cambia precios de uno, de una categoría o de todo el menú.</span></span></li>
                <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="box" size={18}></Icon></span><span><strong>"Se terminó la fugazzeta"</strong><span className="lp-panel-muted">Marca productos como agotados o disponibles.</span></span></li>
                <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="clock" size={18}></Icon></span><span><strong>"Los domingos abrimos de 12 a 16"</strong><span className="lp-panel-muted">Actualiza horarios, dirección y WhatsApp.</span></span></li>
                <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="chart" size={18}></Icon></span><span><strong>"¿Qué fue lo más visto esta semana?"</strong><span className="lp-panel-muted">Te responde sobre tus estadísticas: visitas, escaneos del QR, pedidos y horarios.</span></span></li>
                <li><span className="lp-imp-feat-ic" aria-hidden="true"><Icon name="check" size={18} strokeWidth={2.4}></Icon></span><span><strong>Vos confirmás cada cambio</strong><span className="lp-panel-muted">Te muestra cómo queda y no guarda nada hasta que digas que sí.</span></span></li>
            </ul>
            <a className="lp-btn lp-btn-primary lp-imp-cta" href="#cuenta" onClick={onClose}>Probar Pro gratis {PRO_TRIAL_DAYS} días</a>
            <p className="lp-panel-muted lp-imp-cta-note">Después {PRO_PRICE} por mes. Cancelás cuando quieras.</p>
        </div>
    </Dialog>
);

// Nota de voz del dueño: play/pausa, barra de avance y duración, como en WhatsApp.
const AudioBubble = ({ url, ms }) => {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [current, setCurrent] = useState(0);
    const toggle = () => {
        const audio = ref.current;
        if (!audio) return;
        if (audio.paused) audio.play().catch(() => setPlaying(false)); else audio.pause();
    }
  return (
    <div className="lp-ai-bubble lp-ai-voice">
        <button type="button" className="lp-ai-voice-play" onClick={toggle} aria-label={playing ? "Pausar audio" : "Escuchar audio"}>
            <Icon name={playing ? "pause" : "play"} size={18} strokeWidth={2.4}></Icon>
        </button>
        <span className="lp-ai-voice-track" aria-hidden="true"><span style={{ width: progress * 100 + "%" }}></span></span>
        <span className="lp-ai-voice-time">{clock(playing || current ? current * 1000 : ms)}</span>
        <audio ref={ref} src={url} preload="metadata"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }}
            onTimeUpdate={(e) => {
                const a = e.currentTarget;
                setCurrent(a.currentTime);
                setProgress(ms ? Math.min(1, a.currentTime * 1000 / ms) : 0);
            }}></audio>
    </div>
  )
}

// Botón flotante y chat del asistente (plan Pro). Vive en Account, así la charla no se
// pierde al cambiar de pestaña.
const AssistantPanel = ({ data }) => {
    const pro = data.business.subscribed === true;
    const [open, setOpen] = useState(false);
    const [upsell, setUpsell] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [thinking, setThinking] = useState(false);
    // Grabación: null, o { mode: "hold" (manteniendo apretado) | "locked" (suelto, con botones), ms, cancelHint }
    const [rec, setRec] = useState(null);
    const [voiceError, setVoiceError] = useState("");
    const recorder = useRef(null);
    const gesture = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const voice = canRecord();
    // Estadísticas resumidas para las preguntas tipo "¿qué fue lo más visto?". Se leen al abrir
    // el asistente y se refrescan cada STATS_TTL_MS. { at, promise }
    const stats = useRef(null);
    const getStats = () => {
        const s = stats.current;
        if (s && Date.now() - s.at < STATS_TTL_MS) return s.promise;
        const promise = loadStatsContext(data.business.usuarioEmpresa, data.products);
        stats.current = { at: Date.now(), promise };
        return promise;
    }

    useEffect(() => {
        if (open && pro) getStats();
    }, [open, pro]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("keydown", onKey);
        document.body.classList.add("lp-ai-lock");
        inputRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.classList.remove("lp-ai-lock");
        };
    }, [open]);

    useEffect(() => {
        const list = listRef.current;
        if (list) list.scrollTop = list.scrollHeight;
    }, [messages, thinking, open]);

    // Manda texto o audio. Con audio, el mensaje del dueño muestra la nota de voz y,
    // cuando vuelve la respuesta, lo que entendió debajo.
    const ask = async ({ mensaje, audio }) => {
        if (thinking) return;
        const historial = buildHistory(messages);
        const userId = Date.now() + "-u";
        setMessages((m) => [...m, audio
            ? { id: userId, rol: "usuario", audioUrl: URL.createObjectURL(audio.blob), ms: audio.ms, texto: "" }
            : { id: userId, rol: "usuario", texto: mensaje }]);
        setThinking(true);
        const ctx = { business: data.business, products: data.products, categories: data.categories };
        const context = buildContext(data.business, data.products, data.categories);
        try {
            const idToken = await auth.currentUser.getIdToken();
            const sound = audio ? { type: audio.type, data: await blobToBase64(audio.blob) } : undefined;
            const estadisticas = await getStats();
            const result = await askAssistant({ idToken, mensaje, audio: sound, historial, context, estadisticas });
            const reply = readReply(result, context.refs, ctx);
            const heard = String(result.transcripcion ?? "").trim();
            setMessages((m) => [
                ...m.map((msg) => msg.id === userId && audio ? { ...msg, texto: heard } : msg),
                { id: Date.now() + "-a", rol: "asistente", ...reply }
            ]);
        } catch (error) {
            setMessages((m) => [...m, { id: Date.now() + "-e", rol: "asistente", texto: error.message, error: true }]);
            if (!audio) setText((t) => t || mensaje);
        }
        setThinking(false);
    }

    const send = (value) => {
        const mensaje = value.replace(/\s+/g, " ").trim().slice(0, MAX_MESSAGE);
        if (!mensaje || thinking) return;
        setText("");
        ask({ mensaje }).then(() => inputRef.current?.focus());
    }

    // --- Notas de voz ---

    const stopRecording = async (sendIt) => {
        const r = recorder.current;
        recorder.current = null;
        gesture.current = null;
        setRec(null);
        if (!r) return;
        if (!sendIt) { r.cancel(); return; }
        const audio = await r.stop();
        if (audio.ms < MIN_AUDIO_MS || !audio.blob.size) return;
        ask({ audio });
    }

    // Reloj de la grabación y corte a los 60 segundos.
    useEffect(() => {
        if (!rec) return;
        const timer = setInterval(() => {
            const r = recorder.current;
            if (!r) return;
            const ms = Date.now() - r.startedAt;
            if (ms >= MAX_AUDIO_SECONDS * 1000) stopRecording(true);
            else setRec((s) => s && { ...s, ms });
        }, 200);
        return () => clearInterval(timer);
    }, [!!rec]); // eslint-disable-line react-hooks/exhaustive-deps

    // Si se cierra el asistente grabando, se descarta.
    useEffect(() => {
        if (!open && recorder.current) stopRecording(false);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Como en WhatsApp: mantener apretado graba y al soltar se manda; deslizar a la izquierda
    // cancela y deslizar hacia arriba la deja grabando sola. Un toque corto también la deja grabando.
    const onMicDown = async (e) => {
        if (thinking || recorder.current || gesture.current) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        setVoiceError("");
        gesture.current = { x: e.clientX, y: e.clientY, at: Date.now(), up: false, mode: "hold" };
        setRec({ mode: "hold", ms: 0 });
        try {
            recorder.current = await startRecording();
        } catch (error) {
            gesture.current = null;
            setRec(null);
            setVoiceError(error.message);
            return;
        }
        const g = gesture.current;
        if (!g) { recorder.current?.cancel(); recorder.current = null; return; }
        // Soltó mientras se pedía el permiso: queda grabando con botones.
        if (g.up) { g.mode = "locked"; g.lockedAt = Date.now(); setRec({ mode: "locked", ms: 0 }); }
    }

    const onMicMove = (e) => {
        const g = gesture.current;
        if (!g || g.mode !== "hold" || g.up) return;
        const dx = e.clientX - g.x;
        const dy = e.clientY - g.y;
        if (dx < -90) { stopRecording(false); return; }
        if (dy < -70) { g.mode = "locked"; g.lockedAt = Date.now(); setRec((s) => s && { ...s, mode: "locked" }); return; }
        setRec((s) => s && { ...s, slide: Math.min(0, dx) });
    }

    const onMicUp = () => {
        const g = gesture.current;
        if (!g || g.mode !== "hold") return;
        g.up = true;
        if (!recorder.current) return; // todavía pidiendo permiso
        if (Date.now() - g.at < MIN_AUDIO_MS) {
            g.mode = "locked";
            g.lockedAt = Date.now();
            setRec((s) => s && { ...s, mode: "locked", slide: 0 });
        } else {
            stopRecording(true);
        }
    }

    const setState = (messageId, actionId) => (estado, description) => setMessages((m) => m.map((msg) => msg.id !== messageId ? msg : {
        ...msg,
        acciones: msg.acciones.map((a) => a.id === actionId ? { ...a, estado, description: description || a.description } : a)
    }));

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send(text);
        }
    }

    const ideas = suggestions(data.products, data.categories);
    const showMic = voice && !text.trim();

  return (
    <>
        {!open &&
            <button type="button" className="lp-ai-fab" onClick={() => pro ? setOpen(true) : setUpsell(true)} aria-haspopup="dialog">
                <Icon name="sparkle" size={20}></Icon>
                <span>Asistente</span>
                {!pro && <span className="lp-panel-badge lp-imp-pro">Pro</span>}
            </button>
        }

        <Upsell open={upsell} onClose={() => setUpsell(false)}></Upsell>

        {open &&
            <>
            <div className="lp-ai-scrim" onClick={() => setOpen(false)} aria-hidden="true"></div>
            <section className="lp-ai" role="dialog" aria-modal="true" aria-labelledby="lp-ai-title">
                <header className="lp-ai-head">
                    <span className="lp-ai-avatar" aria-hidden="true"><Icon name="sparkle" size={18}></Icon></span>
                    <div>
                        <h2 id="lp-ai-title">Asistente</h2>
                        <span className="lp-panel-muted">Nada se guarda hasta que confirmes</span>
                    </div>
                    <button type="button" className="lp-panel-icon-btn" onClick={() => setOpen(false)} aria-label="Cerrar el asistente"><Icon name="close"></Icon></button>
                </header>

                <div className="lp-ai-list" ref={listRef} aria-live="polite">
                    {!messages.length &&
                        <div className="lp-ai-empty">
                            <p><strong>¡Hola! Pedime cambios para tu menú.</strong></p>
                            <p className="lp-panel-muted">Puedo cambiar precios, marcar productos agotados, actualizar horarios, dirección y WhatsApp, o activar los pedidos. También respondo sobre tus estadísticas. Escribime o mandame un audio. Antes de guardar te muestro cómo queda.</p>
                            <div className="lp-ai-ideas">
                                {ideas.map((idea) => <button key={idea} type="button" className="lp-panel-chip" onClick={() => send(idea)}>{idea}</button>)}
                            </div>
                        </div>
                    }
                    {messages.map((m) => (
                        <div key={m.id} className={"lp-ai-msg is-" + m.rol + (m.error ? " is-error" : "")}>
                            {m.audioUrl
                                ? <>
                                    <AudioBubble url={m.audioUrl} ms={m.ms}></AudioBubble>
                                    {m.texto && <p className="lp-ai-heard">"{m.texto}"</p>}
                                </>
                                : <p className="lp-ai-bubble">{m.texto}</p>
                            }
                            {m.acciones?.map((a) => <ActionCard key={a.id} action={a} data={data} onState={setState(m.id, a.id)}></ActionCard>)}
                        </div>
                    ))}
                    {thinking &&
                        <div className="lp-ai-msg is-asistente">
                            <p className="lp-ai-bubble lp-ai-typing" aria-label="El asistente está escribiendo"><span></span><span></span><span></span></p>
                        </div>
                    }
                </div>

                {voiceError && <p className="lp-ai-voice-error" role="alert">{voiceError}</p>}

                <form className="lp-ai-form" onSubmit={(e) => { e.preventDefault(); send(text); }}>
                    {rec ?
                        <div className={"lp-ai-rec is-" + rec.mode} role="status" aria-label="Grabando audio">
                            {rec.mode === "locked" && <button type="button" className="lp-ai-rec-trash" onClick={() => stopRecording(false)} aria-label="Descartar audio"><Icon name="trash" size={20}></Icon></button>}
                            <span className="lp-ai-rec-dot" aria-hidden="true"></span>
                            <span className="lp-ai-rec-time">{clock(rec.ms)}</span>
                            {rec.mode === "hold"
                                ? <span className="lp-ai-rec-hint" style={{ transform: `translateX(${rec.slide || 0}px)` }}>‹ Deslizá para cancelar</span>
                                : <span className="lp-ai-rec-hint">Grabando…</span>}
                        </div> :
                        <>
                            <label htmlFor="lp-ai-input" className="lp-panel-sr">Mensaje para el asistente</label>
                            <textarea id="lp-ai-input" ref={inputRef} rows="1" maxLength={MAX_MESSAGE} placeholder={voice ? "Escribí o mandá un audio" : 'Ej.: "subí la muzza un 10%"'} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKeyDown}></textarea>
                        </>
                    }
                    {rec?.mode === "locked" ?
                        <button type="button" className="lp-ai-send" onClick={() => {
                            // El click que sigue al toque que dejó grabando cae sobre este botón: se ignora.
                            if (Date.now() - (gesture.current?.lockedAt || 0) > 400) stopRecording(true);
                        }} aria-label="Enviar audio"><Icon name="send" size={18}></Icon></button> :
                        showMic || rec ?
                            <button type="button" className={"lp-ai-send lp-ai-mic" + (rec ? " is-rec" : "")} disabled={thinking}
                                onPointerDown={onMicDown} onPointerMove={onMicMove} onPointerUp={onMicUp} onPointerCancel={() => stopRecording(false)}
                                onContextMenu={(e) => e.preventDefault()}
                                aria-label="Mantené apretado para grabar un audio, o tocá para grabar"><Icon name="mic" size={20}></Icon></button> :
                            <button type="submit" className="lp-ai-send" disabled={!text.trim() || thinking} aria-label="Enviar"><Icon name="send" size={18}></Icon></button>
                    }
                </form>
            </section>
            </>
        }
    </>
  )
}

export default AssistantPanel
